import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Paper, 
  Grid,
  Card,
  CardContent,
  Divider,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import useCustomerMetadata from '../hooks/useCustomerMetadata';
import { LoadingIndicator, ErrorMessage, StatusIndicator } from '../components/ui';
import MicIcon from '@mui/icons-material/Mic';
import HeadsetIcon from '@mui/icons-material/Headset';
import TranslateIcon from '@mui/icons-material/Translate';
import GroupIcon from '@mui/icons-material/Group';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ChurchIcon from '@mui/icons-material/Church';
import SchoolIcon from '@mui/icons-material/School';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import DevicesIcon from '@mui/icons-material/Devices';
import HearingIcon from '@mui/icons-material/Hearing';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { API_URL } from '../config';

function HomePage() {
  const { isAuthenticated, user, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { metadata, loading, error } = useCustomerMetadata();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isUpgrading, setIsUpgrading] = useState(false);
  // const [showSignupDialog, setShowSignupDialog] = useState(false);

  const organizationName = metadata?.organizationName;
  const hasSubscription = metadata?.hasSubscription;

  // Show signup dialog after a delay for non-authenticated users
  /*
  useEffect(() => {
    if (!isAuthenticated) {
      const timer = setTimeout(() => {
        setShowSignupDialog(true);
      }, 7000); // Show popup after 7 seconds
      
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated]);
  */

  // Handle dialog close
  /*
  const handleCloseDialog = () => {
    setShowSignupDialog(false);
  };
  */

  // Handle signup click
  /*
  const handleSignupClick = () => {
    setShowSignupDialog(false);
    loginWithRedirect();
  };
  */

  const handleUpgradeClick = async () => {
    setIsUpgrading(true);
    try {
      // Check if we have user data
      if (!user || !user.email) {
        throw new Error('User data not available. Please try logging in again.');
      }

      console.log('Starting checkout process...');
      console.log('User data being sent:', {
        email: user.email,
        name: user.name
      });
      
      const response = await fetch(`${API_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          name: user.name || ''
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error response:', errorData);
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      console.log('Checkout response:', data);

      if (!data.url) {
        throw new Error(data.error || 'No checkout URL received');
      }

      window.location.assign(data.url);
    } catch (error) {
      console.error('Checkout error:', error);
      alert(error.message || 'Failed to start checkout process');
    } finally {
      setIsUpgrading(false);
    }
  };

  const features = [
    {
      icon: <MicIcon fontSize="large" color="primary" />,
      title: "Simple Live Broadcasting",
      description: "Easily translate and transcribe your live events with minimal setup and no technical expertise required"
    },
    {
      icon: <TranslateIcon fontSize="large" color="primary" />,
      title: "Multicultural Integration",
      description: "Foster inclusivity with support for 12+ languages, breaking down barriers for diverse communities"
    },
    {
      icon: <AttachMoneyIcon fontSize="large" color="primary" />,
      title: "Affordable Solution",
      description: "Just $100 per month for unlimited translations - no hidden fees or complicated pricing tiers"
    },
    {
      icon: <GroupIcon fontSize="large" color="primary" />,
      title: "Community Connection",
      description: "Build stronger, more inclusive communities by ensuring everyone can participate regardless of language"
    }
  ];

  const idealFor = [
    {
      icon: <ChurchIcon fontSize="large" color="secondary" />, 
      title: "Churches & Ministries",
      description: "Designed for worship services, sermons, and outreach—make every message heard in every language."
    },
    {
      icon: <GroupIcon fontSize="large" color="secondary" />, 
      title: "Multilingual Congregations",
      description: "Unite your church family by providing real-time translation for all members, guests, and visitors."
    },
    {
      icon: <HearingIcon fontSize="large" color="secondary" />, 
      title: "Global Missions",
      description: "Share the Good News with the world—instantly translate your message for remote listeners and international outreach."
    }
  ];

  const howItWorksSteps = [
    {
      icon: <RecordVoiceOverIcon fontSize="large" color="primary" />,
      title: "1. Broadcast Your Voice",
      description: "Simply speak into your microphone and our AI technology captures your voice in real-time"
    },
    {
      icon: <TranslateIcon fontSize="large" color="primary" />,
      title: "2. Instant Translation",
      description: "Our AI translates your speech into multiple languages simultaneously with high accuracy"
    },
    {
      icon: <DevicesIcon fontSize="large" color="primary" />,
      title: "3. Share Access",
      description: "Provide listeners with a simple link they can access from any device - no downloads required"
    },
    {
      icon: <HearingIcon fontSize="large" color="primary" />,
      title: "4. Listen in Any Language",
      description: "Listeners select their preferred language and hear the translation in real-time through their device"
    }
  ];

  return (
    <Box>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Paper 
          elevation={3} 
          sx={{ 
            mt: 4, 
            p: 4, 
            borderRadius: 2,
            background: `linear-gradient(120deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography 
                variant="h2" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 700,
                  fontSize: isMobile ? '2.5rem' : '3.5rem',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Welcome to Glossa.link
              </Typography>
              
              <Typography 
                variant="h5" 
                component="h2" 
                gutterBottom 
                color="text.secondary"
                sx={{ mb: 3, fontWeight: 400 }}
              >
                Make the Gospel accessible in all languages through real-time, AI-powered translation for worship, sermons, and ministry.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<MicIcon />}
                  sx={{ 
                    px: 3, 
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    boxShadow: theme.shadows[4],
                  }}
                  {...(isAuthenticated
                    ? { component: Link, to: "/broadcast" }
                    : { onClick: loginWithRedirect })}
                >
                  {isAuthenticated
                    ? "Start Broadcasting"
                    : "Sign up to Broadcast"}
                </Button>
                <Button
                  component={Link}
                  to="/stream"
                  variant="outlined"
                  size="large"
                  startIcon={<HeadsetIcon />}
                  sx={{ 
                    px: 3, 
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                  }}
                >
                  Start Listening
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={5}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                {isAuthenticated ? (
                  <>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                      Hello, {user?.name || 'User'}
                    </Typography>
                    {loading ? (
                      <LoadingIndicator message="Loading your profile..." size={24} />
                    ) : error ? (
                      <ErrorMessage 
                        title="Error Loading Profile" 
                        message={error} 
                        severity="warning"
                      />
                    ) : (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body1" sx={{ mr: 1 }}>
                            Status:
                          </Typography>
                          <StatusIndicator 
                            status={hasSubscription ? "connected" : "disconnected"} 
                            label={hasSubscription ? "Active Subscription" : "No Subscription"}
                            showTooltip={false}
                          />
                        </Box>
                        {organizationName && (
                          <Typography variant="body1" color="text.primary" gutterBottom>
                            <strong>Organization:</strong> {organizationName}
                          </Typography>
                        )}
                        {metadata && !hasSubscription && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                              You don't have an active subscription
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Unlimited translations for just $100/month
                            </Typography>
                            <Button 
                              variant="contained" 
                              color="primary" 
                              startIcon={<ShoppingCartIcon />}
                              onClick={handleUpgradeClick}
                              disabled={isUpgrading}
                              sx={{ mt: 1 }}
                            >
                              {isUpgrading ? (
                                <>
                                  <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                  Processing...
                                </>
                              ) : 'Subscribe Now'}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                    🎉 14-Day Free Trial 🎉
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Create a free account and subscribe to automatically get a 14-day free trial
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* Free Trial Banner - Only for authenticated, non-subscribed users */}
        {(isAuthenticated && metadata && !hasSubscription) && (
          <Box
            sx={{
              mt: 3, 
              mb: 3, 
              p: 3, 
              borderRadius: 2,
              background: `linear-gradient(120deg, ${theme.palette.secondary.light}40, ${theme.palette.primary.light}40)`,
              border: `2px dashed ${theme.palette.primary.main}`,
              textAlign: 'center'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography 
                variant="h5" 
                component="h2" 
                gutterBottom
                sx={{ 
                  fontWeight: 700,
                  color: theme.palette.primary.main
                }}
              >
                14-Day Free Trial
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Subscribe now to automatically get your 14-day free trial
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleUpgradeClick}
                    sx={{ 
                      mt: 1,
                      px: 4, 
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      boxShadow: theme.shadows[4],
                    }}
              >
                {isUpgrading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Processing...
                  </>
                ) : 'Subscribe Now'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Ideal For Section */}
        <Box sx={{ mt: 6, mb: 6 }}>
          <Typography 
            variant="h4" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ fontWeight: 600, mb: 4 }}
          >
            Designed For
          </Typography>
          
          <Divider sx={{ mb: 4 }} />
          
          <Grid container spacing={3} justifyContent="center">
            {idealFor.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  elevation={2} 
                  sx={{ 
                    height: '100%', 
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: theme.shadows[8],
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box sx={{ mb: 2 }}>
                      {item.icon}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* How It Works Section */}
        <Box sx={{ mt: 6, mb: 6, py: 4, px: 2, bgcolor: `${theme.palette.primary.main}10`, borderRadius: 2 }}>
          <Typography 
            variant="h4" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ fontWeight: 600, mb: 4 }}
          >
            How It Works
          </Typography>
          
          <Divider sx={{ mb: 4 }} />
          
          <Grid container spacing={3} justifyContent="center">
            {howItWorksSteps.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  elevation={2} 
                  sx={{ 
                    height: '100%',
                    position: 'relative',
                    overflow: 'visible',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: theme.shadows[8],
                    }
                  }}
                >
                  {index < howItWorksSteps.length - 1 && !isMobile && (
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        right: -16, 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        zIndex: 1,
                        color: theme.palette.primary.main,
                      }}
                    >
                      <ArrowForwardIcon fontSize="large" />
                    </Box>
                  )}
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box sx={{ mb: 2 }}>
                      {step.icon}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto', mb: 3 }}>
              Our platform is designed to be incredibly simple to use. There's no complicated setup, 
              no special hardware required, and no technical expertise needed. Just $100/month for unlimited translations.
            </Typography>
            
            {!isAuthenticated && (
              <Button
                variant="contained"
                size="large"
                color="primary"
                onClick={() => loginWithRedirect()}
                sx={{ 
                  px: 4, 
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                }}
              >
                Get Started Today
              </Button>
            )}
          </Box>
        </Box>

        {/* Features Section */}
        <Box sx={{ mt: 6, mb: 4 }}>
          <Typography 
            variant="h4" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ fontWeight: 600, mb: 4 }}
          >
            Why Choose Glossa.link?
          </Typography>
          
          <Divider sx={{ mb: 4 }} />
          
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  elevation={2} 
                  sx={{ 
                    height: '100%', 
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: theme.shadows[8],
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box sx={{ mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>  
      </Container>

      {/* Signup Dialog - Only shows for non-authenticated users */}
      {/**
      <Dialog
        open={showSignupDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ 
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 10
        }}>
          <IconButton 
            onClick={handleCloseDialog}
            aria-label="close"
            sx={{
              bgcolor: 'rgba(255,255,255,0.5)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.8)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.light}40 0%, ${theme.palette.secondary.light}40 100%)`,
          opacity: 0.1,
          zIndex: 0
        }} />

        <DialogContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
          <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 700 }}>
            Special Limited-Time Offer
          </Typography>
          
          <Typography variant="h5" align="center" sx={{ mb: 3, color: theme.palette.primary.main, fontWeight: 600 }}>
            Try For Free For 14 Days!
          </Typography>
          
          <Typography variant="body1" align="center" sx={{ mb: 2 }}>
            Sign up now and subscribe to automatically get a 14-day free trial with no charge until the trial ends.
          </Typography>
          
          <Box sx={{ 
            p: 2, 
            m: 3, 
            border: `2px dashed ${theme.palette.primary.main}`,
            borderRadius: 1,
            textAlign: 'center',
            bgcolor: 'background.paper'
          }}>
            <Typography variant="body1" fontWeight={500}>
              Create your free account today and subscribe to start your trial
            </Typography>

          </Box>
          
          <Typography variant="body2" align="center" sx={{ mb: 2, color: 'text.secondary' }}>
            For just $100/month after your free trial, you'll get unlimited broadcasting with real-time translations!
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
          <Button 
            variant="contained" 
            color="primary" 
            size="large" 
            onClick={handleSignupClick}
            sx={{ 
              px: 4, 
              py: 1.2,
              borderRadius: 2,
              fontWeight: 600
            }}
          >
            Sign Up For Free
          </Button>
        </DialogActions>
      </Dialog>
      */}
    </Box>
  );
}

export default HomePage;
